import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getAvatarColor } from '../../utils/avatarColor';
import LogoMark, { sashColorFor, verifiedSchoolOf } from './LogoMark';

// The logo mark is the default identity for a person with no photo (2026-10-15,
// the user's call: a photo stays optional, nobody shows a blank picture). Its
// sash is grey until their university is verified, then their school's colour.
// Teams keep the letter+colour tile (`letter`). Every place that renders one has
// to answer the same three questions (no photo / photo / photo that fails to
// load), and before this component the third answer was "show the browser's
// broken-image glyph", which is how a stale Google or Cloudinary URL turned a
// designed avatar into a torn-page icon.
//
// `onError` is the whole point: a URL that 404s, gets rate-limited, or is
// blocked by a referrer policy falls back to exactly what an account with no
// photo shows, so a dead link is indistinguishable from never having uploaded.
//
// The logo sits on press.paper-2 (#F4F4F5), set inline so it beats a caller's
// dark letterClassName background: the black mark would vanish on ink.
const LOGO_TILE_BG = '#F4F4F5';
export function avatarLetterFor(user) {
  return (
    user?.firstName?.charAt(0)?.toUpperCase() ||
    user?.username?.charAt(0)?.toUpperCase() ||
    user?.email?.charAt(0)?.toUpperCase() ||
    'U'
  );
}

// `size` and `className` are alternatives, not partners. Pass a number and the
// dimensions are set inline; pass classes instead and they own the sizing and
// shape completely. That second mode is what lets this replace the hand-rolled
// <img> in call sites that already have their own Tailwind classes and rings —
// inline styles beat classes in the cascade, so a hardcoded width here would
// silently defeat theirs.
export default function Avatar({ user, size, border, style, className, letterClassName, letter: asLetter }) {
  const photo = user?.profilePhoto;
  const [failed, setFailed] = useState(false);

  // A changed URL deserves a fresh attempt — otherwise uploading a new photo
  // after a broken one would stay stuck on the letter fallback until reload.
  useEffect(() => { setFailed(false); }, [photo]);

  const letter = avatarLetterFor(user);
  const sized = typeof size === 'number';
  const base = {
    ...(sized ? { width: `${size}px`, height: `${size}px`, borderRadius: '50%' } : {}),
    flexShrink: 0,
    ...(border ? { border } : {}),
    ...style,
  };

  if (photo && !failed) {
    return (
      <img
        src={photo}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className={className || undefined}
        style={{ ...base, objectFit: 'cover' }}
      />
    );
  }

  if (!asLetter) {
    return (
      <div
        aria-hidden="true"
        className={letterClassName || className || undefined}
        style={{
          ...base,
          background: LOGO_TILE_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <LogoMark sash={sashColorFor(verifiedSchoolOf(user))} />
      </div>
    );
  }

  return (
    <div
      // Decorative: the adjacent name is the accessible label in every place
      // this renders, so announcing a bare letter would just be noise.
      aria-hidden="true"
      className={letterClassName || className || undefined}
      style={{
        ...base,
        // Passing letterClassName means the caller is styling the fallback
        // themselves. Setting a colour inline here would beat their classes in
        // the cascade, so step aside and let them own it.
        ...(letterClassName ? {} : { background: getAvatarColor(user?.firstName, user?.lastName) }),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...(sized ? { fontSize: `${Math.round(size * 0.39)}px` } : {}),
        fontWeight: 700,
        color: '#fff',
        fontFamily: '"Instrument Sans", "Segoe UI", system-ui, sans-serif',
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {letter}
    </div>
  );
}

Avatar.propTypes = {
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    profilePhoto: PropTypes.string,
  }),
  size: PropTypes.number,
  border: PropTypes.string,
  style: PropTypes.object,
  className: PropTypes.string,
  // Only needed when the photo and the letter want different classes — e.g. the
  // letter needs a background utility the photo must not have.
  letterClassName: PropTypes.string,
  // Letter+colour tile instead of the logo, for things that aren't a person
  // (team tiles).
  letter: PropTypes.bool,
};

Avatar.defaultProps = {
  user: null,
  // Null, not a number: a default size would set inline dimensions on every
  // className-only call site and quietly beat the classes it was given.
  size: null,
  border: null,
  style: null,
  className: null,
  letterClassName: null,
  letter: false,
};
